import type { RealtimeTimeseriesPoint } from '@shared/api-client';
import { formatCompactNumber } from '@/lib/utils';

const WIDTH = 900;
const HEIGHT = 280;
const PADDING_X = 18;
const PADDING_Y = 24;

const formatTime = (value: string) =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });

export const RealtimeLineChart = ({
  points,
}: {
  points: RealtimeTimeseriesPoint[];
}) => {
  const values = points.map((point) => point.visitors);
  const normalized = values.length > 1 ? values : [values[0] ?? 0, 0];
  const maximum = Math.max(...normalized, 1);
  const chartPoints = normalized.map((value, index) => ({
    x: PADDING_X + (index / (normalized.length - 1)) * (WIDTH - PADDING_X * 2),
    y: PADDING_Y + ((maximum - value) / maximum) * (HEIGHT - PADDING_Y * 2),
    value,
  }));
  const line = chartPoints
    .map(({ x, y }, index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`)
    .join(' ');
  const area = `${line} L ${chartPoints.at(-1)?.x ?? WIDTH - PADDING_X} ${HEIGHT - PADDING_Y} L ${PADDING_X} ${HEIGHT - PADDING_Y} Z`;
  const labelIndexes = Array.from(
    new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]),
  ).filter((index) => index >= 0 && points[index]);

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-[260px] w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Active visitors over the last 30 minutes"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = PADDING_Y + fraction * (HEIGHT - PADDING_Y * 2);
          return (
            <g key={fraction}>
              <line
                x1={PADDING_X}
                x2={WIDTH - PADDING_X}
                y1={y}
                y2={y}
                className="stroke-border"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={PADDING_X + 4}
                y={Math.max(y - 6, 12)}
                className="fill-ink-subtle text-[10px]"
              >
                {formatCompactNumber(Math.round(maximum * (1 - fraction)))}
              </text>
            </g>
          );
        })}

        <path d={area} className="fill-accent/8" />
        <path
          d={line}
          fill="none"
          className="stroke-accent"
          strokeWidth="2.25"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {chartPoints.map((point, index) => (
          <circle
            key={`${point.x}-${point.y}`}
            cx={point.x}
            cy={point.y}
            r="7"
            className="fill-transparent"
          >
            <title>
              {points[index]
                ? `${formatTime(points[index].timestamp)}: ${formatCompactNumber(point.value)} active`
                : formatCompactNumber(point.value)}
            </title>
          </circle>
        ))}
      </svg>

      <div className="mt-1 flex justify-between px-4 text-xs text-ink-subtle">
        {labelIndexes.map((index) => (
          <span key={points[index].timestamp}>
            {formatTime(points[index].timestamp)}
          </span>
        ))}
      </div>
    </div>
  );
};
